// EDEN · small shared UI builders for the 2026-09-30 redesign (pure HTML strings, no lifecycle —
// they work with the router's data-link click delegation like everything else). One look for the
// things every screen repeats: the page header, a plan's status, an empty state, a big material
// button, a loading skeleton, a quiet toast.

import { esc, notify } from "./dom";
import { icon } from "./icons";

export type PlanState = "none" | "not_started" | "in_progress" | "submitted" | "approved";

/** A plan's status as one calm chip. Neutral colours only — never red (no-shaming rail). */
export function statusChip(state: PlanState, opts: { lead?: boolean } = {}): string {
  switch (state) {
    case "approved":
      return `<span class="chip st st-approved approved">${icon("check")}Approved</span>`;
    case "submitted":
      return `<span class="chip st st-submitted submitted">${opts.lead ? "To review" : "Submitted"}</span>`;
    case "in_progress":
    case "not_started":
      return `<span class="chip st st-draft">Draft</span>`;
    default:
      return `<span class="chip st st-none">Not planned</span>`;
  }
}

/** The strongest state among several lessons (one class can hold more than one plan a week). */
export function bestState(states: PlanState[]): PlanState {
  const order: PlanState[] = ["approved", "submitted", "in_progress", "not_started", "none"];
  for (const s of order) if (states.includes(s)) return s;
  return "none";
}

export interface HeaderOpts {
  eyebrow?: string; // small line above the title (plain text, escaped)
  title: string; // plain text, escaped
  sub?: string; // one short line under the title (HTML allowed — caller escapes)
  actions?: string; // right-aligned buttons (HTML)
  crumbs?: { label: string; href?: string }[]; // a short "where am I" trail above everything
}

/** The page header every redesigned screen opens with: trail · eyebrow · H1 · one line · actions. */
export function pageHeader(o: HeaderOpts): string {
  const crumbs = o.crumbs?.length
    ? `<nav class="crumbs" aria-label="Breadcrumb">${o.crumbs
        .map((c, i) => {
          const last = i === o.crumbs!.length - 1;
          const el = c.href && !last
            ? `<a data-link href="${esc(c.href)}">${esc(c.label)}</a>`
            : `<span${last ? ' aria-current="page"' : ""}>${esc(c.label)}</span>`;
          return (i ? `<span class="sep" aria-hidden="true">›</span>` : "") + el;
        })
        .join("")}</nav>`
    : "";
  return `<header class="page-head">
    ${crumbs}
    <div class="page-head-row">
      <div class="page-head-text">
        ${o.eyebrow ? `<div class="page-eyebrow">${esc(o.eyebrow)}</div>` : ""}
        <h1>${esc(o.title)}</h1>
        ${o.sub ? `<p class="page-sub">${o.sub}</p>` : ""}
      </div>
      ${o.actions ? `<div class="page-actions">${o.actions}</div>` : ""}
    </div>
  </header>`;
}

/** A friendly empty state: one line of what is missing and, when there is one, what to do. */
export function emptyState(text: string, action = ""): string {
  return `<div class="empty-state"><p>${text}</p>${action}</div>`;
}

/** A big button that opens a teaching file (worksheet, Teacher Pack, a required PDF) in a new tab. */
export function fileButton(href: string, label: string, kind: "sheet" | "pack" | "pdf" = "sheet", extra = ""): string {
  // (.matlink: the class every material link has carried since the Week card — the service-worker walk
  // finds the Week card's sheet/pack by it)
  return `<a class="filebtn filebtn-${kind} matlink" href="${esc(href)}" target="_blank" rel="noopener"${extra}>
    ${icon(kind === "pack" ? "pack" : kind === "pdf" ? "pdf" : "sheet")}
    <span>${esc(label)}</span>
    ${icon("external", "ico-sm")}
  </a>`;
}

/** The loading skeleton the router shows while a page's data is on its way. */
export function skeletonHtml(title: string): string {
  const bar = (w: number, h = 14): string => `<span class="sk" style="width:${w}%;height:${h}px"></span>`;
  return `<div class="page skeleton" aria-hidden="true">
    <header class="page-head"><div class="page-head-row"><div class="page-head-text">
      <h1 class="sk-title">${esc(title)}</h1>
      ${bar(38, 12)}
    </div></div></header>
    <div class="sk-card">${bar(30, 12)}${bar(70, 22)}${bar(92)}${bar(84)}<div class="sk-row">${bar(22, 40)}${bar(22, 40)}</div></div>
    <div class="sk-card">${bar(26, 12)}${bar(64, 22)}${bar(88)}</div>
  </div>`;
}

/** A quiet toast at the bottom of the screen. Calm confirmations only — never an alarm. */
export function toast(message: string): void {
  notify(message);
}

/** "Mon 28 Sep" from YYYY-MM-DD (UTC-noon anchored — never a local-time date shift). */
export function dayLabel(iso: string): string {
  const [y, m, d] = iso.slice(0, 10).split("-").map(Number) as [number, number, number];
  const dt = new Date(Date.UTC(y, m - 1, d, 12));
  const wd = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"][dt.getUTCDay()];
  const mo = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"][m - 1];
  return `${wd} ${d} ${mo}`;
}

/** "28 Sep – 4 Oct" for a week range. */
export function rangeLabel(start: string, end: string): string {
  const f = (iso: string): string => {
    const [, m, d] = iso.slice(0, 10).split("-").map(Number) as [number, number, number];
    return `${d} ${["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"][m - 1]}`;
  };
  return `${f(start)} – ${f(end)}`;
}

/** Days from today (local date) to an ISO date: 0 today, 1 tomorrow, negative in the past. */
export function daysFromToday(iso: string): number {
  const now = new Date();
  const today = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
  const [y, m, d] = iso.slice(0, 10).split("-").map(Number) as [number, number, number];
  return Math.round((Date.UTC(y, m - 1, d) - today) / 86_400_000);
}

/** Tabs that are plain links (each tab is its own URL, so Back and bookmarks work). */
export function linkTabs(tabs: { label: string; href: string; on: boolean; count?: number }[], label = "Sections"): string {
  return `<nav class="tabs" aria-label="${esc(label)}">${tabs
    .map((tb) => `<a class="tab-link${tb.on ? " on" : ""}" data-link href="${esc(tb.href)}"${tb.on ? ' aria-current="page"' : ""}>${esc(tb.label)}${
      tb.count !== undefined ? ` <span class="count">${tb.count}</span>` : ""
    }</a>`)
    .join("")}</nav>`;
}
